import { ChangeEvent, FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { isNotEmpty } from '../../../../../_metronic/helpers'
import { initialBalance, Balance } from '../core/_models'
import { useListView } from '../core/ListViewProvider'
import { createBalance, updateBalance } from '../core/_requests'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { FormattedMessage, useIntl } from 'react-intl'
import axios from 'axios'
import Select from 'react-select'
import { PaymentMethod } from '../../../paymentmethod/paymentmethods-list/core/_models'
import { Currency } from '../../../language/languages-list/core/_models'
import { Reseller } from '../../../reseller/resellers-list/core/_models'
import { BalancesListLoading } from '../components/loading/BalancesListLoading'

type Props = {
  isBalanceLoading: boolean
  balance: Balance
}

const editBalanceSchema = Yup.object().shape({
  reseller_id: Yup.string().required('Reseller is required'),
  transaction_type: Yup.string().oneOf(['credit', 'debit']),
  balance_amount: Yup.number().required('Balance amount is required').min(0.01, 'Amount must be positive'),
  currency_id: Yup.string().required('Currency is required'),
  // payment_method_id: Yup.string().nullable(),
  // payment_amount: Yup.number().nullable(),
  // payment_currency_id: Yup.string().nullable(),
  // payment_status: Yup.string().oneOf(['completed', 'pending', 'failed']).nullable(),
  // payment_notes: Yup.string().nullable(),
  description: Yup.string().nullable(),
  // payment_date: Yup.date().nullable(),
})

const BalanceEditModalForm: FC<Props> = ({ balance, isBalanceLoading }) => {
  const API_URL = process.env.REACT_APP_THEME_API_URL
  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [resellers, setResellers] = useState<Reseller[]>([])
  const [resellerBalance, setResellerBalance] = useState<any>(null)
  const [resellerPayment, setResellerPayment] = useState<any>(null)
  const [resellerLoan, setResellerLoan] = useState<any>(null)
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [isCurrencyDisabled, setIsCurrencyDisabled] = useState(false)
  const [transactionType, setTransactionType] = useState('credit')

  useEffect(() => {
    axios.get(`${API_URL}/admin/paymentmethods`).then((response) => {
      if (response.data.success) {
        setPaymentMethods(response.data.data.paymentmethods)
      }
    }).catch((error) => console.error('Error fetching payment methods:', error))

    axios.get(`${API_URL}/admin/resellers`).then((response) => {
      if (response.data.success) {
        setResellers(response.data.data.resellers)
      }
    }).catch((error) => console.error('Error fetching resellers:', error))

    axios.get(`${API_URL}/admin/currencies`).then((response) => {
      if (response.data.success) {
        setCurrencies(response.data.data.currencies)
      }
    }).catch((error) => console.error('Error fetching currencies:', error))
  }, [API_URL])

  const handleResellerChange = async (selectedOption: any) => {
    const resellerId = selectedOption?.value
    formik.setFieldValue('reseller_id', resellerId)

    try {
      const resellerResponse = await axios.get(`${API_URL}/admin/resellers/${resellerId}`)
      const resellerData = resellerResponse.data?.data?.reseller
      setResellerBalance(resellerData.balance + " " + resellerData.currency.code)
      setResellerPayment(resellerData.total_balance_sent - resellerData.total_payments_received > 0 ? resellerData.total_balance_sent - resellerData.total_payments_received : 0 + " " + resellerData.currency.code)
      setResellerLoan(resellerData.total_balance_sent - resellerData.total_payments_received + " " + resellerData.currency.code)
      if (resellerData?.currency.id) {
        formik.setFieldValue('currency_id', resellerData.currency.id)
        formik.setFieldValue('payment_currency_id', resellerData.currency.id)
        setIsCurrencyDisabled(true)
      } else {
        setIsCurrencyDisabled(false)
      }
    } catch (error) {
      console.error('Error fetching reseller currency preference:', error)
    }
  }

  const handleTransactionTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    setTransactionType(value)
    formik.setFieldValue('transaction_type', value)

    // Clear payment fields if transaction type is debit
    if (value === 'debit') {
      formik.setFieldValue('payment_method_id', null)
      formik.setFieldValue('payment_amount', null)
      formik.setFieldValue('payment_status', null)
      formik.setFieldValue('payment_notes', null)
      formik.setFieldValue('payment_date', null)
    }
  }

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: {
      ...balance,
      reseller_id: balance.reseller_id || initialBalance.reseller_id,
      transaction_type: balance.transaction_type || initialBalance.transaction_type,
      balance_amount: balance.balance_amount || initialBalance.balance_amount,
      currency_id: balance.currency_id || initialBalance.currency_id,
      description: balance.description || initialBalance.description,
      payment_method_id: balance.payment_method_id || initialBalance.payment_method_id,
      payment_amount: balance.payment_amount || initialBalance.payment_amount,
      payment_currency_id: balance.payment_currency_id || initialBalance.payment_currency_id,
      payment_status: balance.payment_status || initialBalance.payment_status,
      payment_notes: balance.payment_notes || initialBalance.payment_notes,
      payment_date: balance.payment_date || initialBalance.payment_date,
      balance_date: balance.balance_date || initialBalance.balance_date,
    },
    validationSchema: editBalanceSchema,
    validateOnChange: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          await updateBalance(values)
        } else {
          await createBalance(values)
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  const resellerOptions = resellers.map((reseller) => ({
    value: reseller.id,
    label: reseller.reseller_name,
  }))

  const intl = useIntl();
  return (
    <>
      {/* ✅ Display Reseller Balance and Payment Amount in a Single Row */}
      {resellerBalance !== null && resellerPayment !== null && (
        <div
          className="d-flex justify-content-between align-items-center p-3 shadow-sm rounded border"
          style={{
            background: 'linear-gradient(to right, #f9fafb, #eef2f7)',
            border: '2px solid #d1d5db',
            maxWidth: '100%',
          }}
        >
          {/* 💰 Reseller Balance */}
          <div className="d-flex flex-column text-center">
            <h5 className="fw-bold text-dark mb-1">
              <FormattedMessage id='BALANCE.FORM.RESELLER.BALANCE' defaultMessage='موجودی فعلی مشتری شما' />
            </h5>
            <h3 className="fw-bold text-success">{resellerBalance}</h3>
          </div>

          {/* 🔻 Divider */}
          <div className="border-start border-secondary px-3" style={{ height: '40px' }}></div>

          {/* 💳 Reseller Payment */}
          <div className="d-flex flex-column text-center">
            <h5 className="fw-bold text-dark mb-1">
              <FormattedMessage id='BALANCE.FORM.RESELLER.PAYMENT' defaultMessage='پرداخت فعلی مشتری شما' />
            </h5>
            <h3 className="fw-bold text-danger">{resellerPayment}</h3>
          </div>
          {/* 💳 Reseller Payment */}
          <div className="d-flex flex-column text-center">
            <h5 className="fw-bold text-dark mb-1">
              <FormattedMessage id='BALANCE.FORM.RESELLER.PAYMENT' defaultMessage='قرضداری فعلی مشتری شما' />
            </h5>
            <h3 className="fw-bold text-info">{resellerLoan}</h3>
          </div>
        </div>
      )}


      <form id="kt_modal_add_balance_form" className="form" onSubmit={formik.handleSubmit} noValidate>
        <div className="row">
          {/* Balance Section */}
          <div className="col-lg-6 border p-3 mb-3">
            <h4 className="text-primary mb-3">
              {intl.formatMessage({ id: 'BALANCE.DETAILS.TITLE', defaultMessage: 'Balance Details' })}
            </h4>

            <div className="fv-row mb-3">
              <label className="required fw-bold fs-6 mb-1">
                {intl.formatMessage({ id: 'BALANCE.FORM.RESELLER.LABEL', defaultMessage: 'Reseller' })}
              </label>
              <Select
                options={resellerOptions}
                onChange={handleResellerChange}
                isDisabled={formik.isSubmitting || isBalanceLoading}
                placeholder={intl.formatMessage({
                  id: 'BALANCE.FORM.RESELLER.PLACEHOLDER',
                  defaultMessage: 'Select a reseller',
                })}
                className={clsx(
                  { 'is-invalid': formik.touched.reseller_id && formik.errors.reseller_id },
                  { 'is-valid': formik.touched.reseller_id && !formik.errors.reseller_id }
                )}
                value={resellerOptions.find((option) => option.value === formik.values.reseller_id)}
              />
            </div>

            <div className="fv-row mb-3">
              <label className="required fw-bold fs-6 mb-1">
                {intl.formatMessage({
                  id: 'BALANCE.FORM.TRANSACTIONTYPE.LABEL',
                  defaultMessage: 'Transaction Type',
                })}
              </label>
              <select
                {...formik.getFieldProps('transaction_type')}
                className="form-select form-control"
                onChange={handleTransactionTypeChange}
              >
                <option selected disabled>
                  {intl.formatMessage({
                    id: 'BALANCE.FORM.TRANSACTIONTYPE.PLACEHOLDER',
                    defaultMessage: 'Select',
                  })}
                </option>
                <option value="credit">
                  {intl.formatMessage({
                    id: 'BALANCE.FORM.TRANSACTIONTYPE.CREDIT',
                    defaultMessage: 'میخواهم به حساب مشتری اضافه کنم',
                  })}
                </option>
                <option value="debit">
                  {intl.formatMessage({
                    id: 'BALANCE.FORM.TRANSACTIONTYPE.DEBIT',
                    defaultMessage: 'میخواهم از حساب مشتری کم کنم',
                  })}
                </option>
              </select>
            </div>

            <div className="fv-row mb-3">
              <label className="required fw-bold fs-6 mb-1">
                {intl.formatMessage({
                  id: 'BALANCE.FORM.AMOUNT.LABEL',
                  defaultMessage: 'Balance Amount',
                })}
              </label>
              <input
                {...formik.getFieldProps('balance_amount')}
                className={clsx(
                  'form-control form-control-solid',
                  { 'is-invalid': formik.touched.balance_amount && formik.errors.balance_amount },
                  { 'is-valid': formik.touched.balance_amount && !formik.errors.balance_amount }
                )}
                type="number"
                disabled={formik.isSubmitting || isBalanceLoading}
              />
            </div>

            <div className="fv-row mb-3">
              <label className="required fw-bold fs-6 mb-1">
                {intl.formatMessage({ id: 'BALANCE.FORM.CURRENCY.LABEL', defaultMessage: 'Currency' })}
              </label>
              <select
                {...formik.getFieldProps('currency_id')}
                className="form-select form-control"
                disabled={!currencies.length || isCurrencyDisabled}
              >
                <option value="" disabled>
                  {intl.formatMessage({
                    id: 'BALANCE.FORM.CURRENCY.PLACEHOLDER',
                    defaultMessage: 'Select',
                  })}
                </option>
                {currencies.map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.name} - {currency.code}
                  </option>
                ))}
              </select>
            </div>

            <div className="fv-row mb-3">
              <label className="fw-bold fs-6 mb-1">
                {intl.formatMessage({ id: 'BALANCE.FORM.DESCRIPTION.LABEL', defaultMessage: 'Description' })}
              </label>
              <textarea
                {...formik.getFieldProps('description')}
                className="form-control form-control-solid"
                placeholder={intl.formatMessage({
                  id: 'BALANCE.FORM.DESCRIPTION.PLACEHOLDER',
                  defaultMessage: 'Enter description',
                })}
                style={{ height: 'calc(1.5em + 0.75rem + 2px)', resize: 'none' }}
                disabled={formik.isSubmitting || isBalanceLoading}
              />
            </div>
          </div>

          {/* Payment Section - Conditional Rendering */}
          {transactionType === 'credit' && (
            <div className="col-lg-6 border p-3 mb-3">
              <h4 className="text-primary mb-3">
                {intl.formatMessage({ id: 'PAYMENT.DETAILS.TITLE', defaultMessage: 'Payment Details' })}
              </h4>

              <div className="fv-row mb-3">
                <label className="fw-bold fs-6 mb-1">
                  {intl.formatMessage({ id: 'PAYMENT.FORM.METHOD.LABEL', defaultMessage: 'Payment Method' })}
                </label>
                <select
                  {...formik.getFieldProps('payment_method_id')}
                  className="form-select form-control"
                  disabled={formik.isSubmitting || isBalanceLoading}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'PAYMENT.FORM.METHOD.PLACEHOLDER', defaultMessage: 'Select' })}
                  </option>
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.method_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fv-row mb-3">
                <label className="fw-bold fs-6 mb-1">
                  {intl.formatMessage({ id: 'PAYMENT.FORM.AMOUNT.LABEL', defaultMessage: 'Payment Amount' })}
                </label>
                <input
                  {...formik.getFieldProps('payment_amount')}
                  className="form-control form-control-solid"
                  type="number"
                  disabled={formik.isSubmitting || isBalanceLoading}
                />
              </div>

              <div className="fv-row mb-3">
                <label className="fw-bold fs-6 mb-1">
                  {intl.formatMessage({ id: 'PAYMENT.FORM.CURRENCY.LABEL', defaultMessage: 'Payment Currency' })}
                </label>
                <select
                  {...formik.getFieldProps('payment_currency_id')}
                  className="form-select form-control"
                  disabled={!currencies.length || isCurrencyDisabled}
                >
                  <option value="" disabled>
                    {intl.formatMessage({
                      id: 'PAYMENT.FORM.CURRENCY.PLACEHOLDER',
                      defaultMessage: 'Select',
                    })}
                  </option>
                  {currencies.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.name} - {currency.code}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fv-row mb-3">
                <label className="fw-bold fs-6 mb-1">
                  {intl.formatMessage({ id: 'PAYMENT.FORM.NOTES.LABEL', defaultMessage: 'Payment Notes' })}
                </label>
                <textarea
                  {...formik.getFieldProps('payment_notes')}
                  className="form-control form-control-solid"
                  placeholder={intl.formatMessage({
                    id: 'PAYMENT.FORM.NOTES.PLACEHOLDER',
                    defaultMessage: 'Enter payment notes',
                  })}
                  style={{ height: 'calc(1.5em + 0.75rem + 2px)', resize: 'none' }}
                  disabled={formik.isSubmitting || isBalanceLoading}
                />
              </div>

              <div className="fv-row mb-3">
                <label className="fw-bold fs-6 mb-1">
                  {intl.formatMessage({ id: 'PAYMENT.FORM.DATE.LABEL', defaultMessage: 'Payment Date' })}
                </label>
                <input
                  {...formik.getFieldProps('payment_date')}
                  className="form-control form-control-solid"
                  type="date"
                  disabled={formik.isSubmitting || isBalanceLoading}
                />
              </div>
            </div>
          )}
        </div>

        <div className="text-center pt-15">
          <button
            type="reset"
            onClick={() => cancel()}
            className="btn btn-light me-3"
            disabled={formik.isSubmitting || isBalanceLoading}
          >
            {intl.formatMessage({ id: 'FORM.GENERAL.DISCARD', defaultMessage: 'Discard' })}
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isBalanceLoading || formik.isSubmitting || !formik.isValid}
          >
            <span className="indicator-label">
              {intl.formatMessage({ id: 'FORM.GENERAL.SUBMIT', defaultMessage: 'Submit' })}
            </span>
            {formik.isSubmitting && (
              <span className="indicator-progress">
                {intl.formatMessage({ id: 'FORM.GENERAL.PLEASEWAIT', defaultMessage: 'Please wait...' })}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {isBalanceLoading && <BalancesListLoading />}
    </>
  );
}

export { BalanceEditModalForm }
